import { useId } from 'react'
import { AppVersion } from '../components/AppVersion.js'
import { SaveStatus } from '../features/boards/SaveStatus.js'
import { FeedbackTab } from '../features/feedback/FeedbackTab.js'
import { useBoardStore } from '../state/board-store.js'
import { DEFAULT_ZOOM, ZOOM_STEPS, useUiStore } from '../state/ui-store.js'
import styles from './FooterBar.module.css'

/**
 * Status strip under the board: attribution on the left, controls and status on the
 * right, so the board keeps its full width and the eye lands on the save state.
 */
export function FooterBar() {
  return (
    <footer className={styles.bar}>
      <span className={styles.copyright}>
        <a className={styles.link} href="https://teamcentric.io" target="_blank" rel="noreferrer noopener">
          Teamcentric
        </a>{' '}
        &copy; 2026
      </span>
      <AppVersion className={styles.copyright} />
      <DurabilityNote />
      <span className={styles.spacer} />
      <BoardLinkSync />
      <QuickInsertToggle />
      <ZoomControl />
      <SaveStatus />
      <MinimapToggle />
      <FeedbackTab />
    </footer>
  )
}

/**
 * What the storage behind this board actually guarantees. The repository reports it, so
 * memory storage is named as temporary instead of implying a refresh is survivable.
 */
function DurabilityNote() {
  const durability = useBoardStore((state) => state.durability)
  if (!durability) return null
  return (
    <span className={styles.durability} data-testid="durability">
      {durability}
    </span>
  )
}

function ZoomControl() {
  const zoom = useUiStore((state) => state.zoom)
  const setZoom = useUiStore((state) => state.setZoom)
  const index = ZOOM_STEPS.indexOf(zoom as (typeof ZOOM_STEPS)[number])
  // An unrecognised stored value would give -1; treat it as the default step.
  const step = index === -1 ? ZOOM_STEPS.indexOf(DEFAULT_ZOOM as (typeof ZOOM_STEPS)[number]) : index

  return (
    <span className={styles.zoom}>
      <button
        type="button"
        className={styles.iconToggle}
        aria-label="Zoom out"
        title="Zoom out"
        disabled={step <= 0}
        onClick={() => setZoom(ZOOM_STEPS[step - 1] ?? DEFAULT_ZOOM)}
      >
        &#8722;
      </button>
      <button
        type="button"
        className={styles.zoomValue}
        title="Reset zoom to 100%"
        onClick={() => setZoom(DEFAULT_ZOOM)}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        className={styles.iconToggle}
        aria-label="Zoom in"
        title="Zoom in"
        disabled={step >= ZOOM_STEPS.length - 1}
        onClick={() => setZoom(ZOOM_STEPS[step + 1] ?? DEFAULT_ZOOM)}
      >
        &#43;
      </button>
    </span>
  )
}

function MinimapToggle() {
  const minimapOpen = useUiStore((state) => state.minimapOpen)
  const setMinimapOpen = useUiStore((state) => state.setMinimapOpen)

  return (
    <button
      type="button"
      className={styles.iconToggle}
      aria-pressed={minimapOpen}
      aria-label={minimapOpen ? 'Hide minimap' : 'Show minimap'}
      title={minimapOpen ? 'Hide minimap' : 'Show minimap'}
      onClick={() => setMinimapOpen(!minimapOpen)}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4.5" width="18" height="15" rx="2" />
        <rect x="6" y="7.5" width="7" height="6" rx="1" />
      </svg>
    </button>
  )
}

function QuickInsertToggle() {
  const quickInsert = useUiStore((state) => state.quickInsert)
  const setQuickInsert = useUiStore((state) => state.setQuickInsert)
  const id = useId()

  return (
    <label className={styles.toggle} htmlFor={id} title="Add cards straight onto the board and name them in place, instead of opening the card editor">
      <input
        id={id}
        type="checkbox"
        checked={quickInsert}
        onChange={(event) => setQuickInsert(event.target.checked)}
      />
      Quick Insert
    </label>
  )
}

/** Shown only on a story map generated from a mindmap. */
function BoardLinkSync() {
  const linked = useBoardStore((state) => Boolean(state.entities?.board.source?.mindmapId))
  return linked ? <LinkSyncToggles /> : null
}

/**
 * Whether label and colour edits are carried between a mindmap and the story maps made
 * from it. One setting for this browser, shown in both footers.
 */
export function LinkSyncToggles() {
  const linkSync = useUiStore((state) => state.linkSync)
  const setLinkSync = useUiStore((state) => state.setLinkSync)
  const labelsId = useId()
  const coloursId = useId()

  return (
    <>
      <label className={styles.toggle} htmlFor={labelsId} title="Renaming here also renames the linked mindmap node or story map items">
        <input id={labelsId} type="checkbox" checked={linkSync.labels} onChange={(event) => setLinkSync({ labels: event.target.checked })} />
        Sync labels
      </label>
      <label className={styles.toggle} htmlFor={coloursId} title="Recolouring here also recolours the linked mindmap node or cards">
        <input id={coloursId} type="checkbox" checked={linkSync.colours} onChange={(event) => setLinkSync({ colours: event.target.checked })} />
        Sync colours
      </label>
    </>
  )
}
